import { Skeleton } from "@mui/material"
import React from "react"
import NoData from "./NoData"
import PaginationButtons from "./PaginationButtons"
import GlobalDivider from "../Global/GlobalDivider"

type Props = {
    rows: any[]
    isLoading: boolean | undefined
    headCells: { id: string; label: string; dataClass: string }[]
    totalPages: number
    handlePageChange: (num: number) => void
    page: number
    tableTitle?: string
}

const GlobalTable = ({
    rows = [],
    isLoading,
    headCells = [],
    handlePageChange,
    totalPages,
    page,
    tableTitle,
}: Props) => {
    return (
        <div className="w-full">
            <div className="w-full h-full">
                {/* ✅ Desktop / Tablet Table with Horizontal Scroll */}
                <div className="hidden overflow-x-auto w-full md:block">
                    <table className="border-collapse table-auto">
                        <thead>
                            <tr
                                className={`${rows.length > 0 ? "text-white" : "text-textSecondary"
                                    }`}
                            >
                                {headCells?.map((cell, index) => (
                                    <th
                                        key={index}
                                        className={`text-left font-semibold text-sm px-3 py-2 whitespace-nowrap ${cell.dataClass}`}
                                    >
                                        {cell.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        {isLoading ? (
                            <tbody>
                                <tr className="text-sm font-medium">
                                    {headCells?.map((cell, index) => (
                                        <td
                                            key={index}
                                            className="px-3 py-3 text-xs font-medium whitespace-nowrap text-textSecondary"
                                        >
                                            <Skeleton
                                                variant="rounded"
                                                width={"80%"}
                                                height={20}
                                                animation="wave"
                                            />
                                        </td>
                                    ))}
                                </tr>
                            </tbody>
                        ) : (
                            <tbody>
                                {rows?.map((row, index) => (
                                    <tr key={index} className="text-sm font-medium">
                                        {headCells?.map((cell, idx) => (
                                            <td
                                                key={idx}
                                                className={`font-medium text-left text-sm text-textSecondary py-3 px-3 whitespace-nowrap ${headCells[idx].dataClass}`}
                                            >
                                                {row[cell.id]}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        )}
                    </table>
                </div>

                {/* ✅ Mobile View → stacked cards */}
                <div className="flex flex-col gap-3 md:hidden">
                    <div className="flex flex-col gap-2 w-full text-sm text-white">
                        {isLoading &&
                            headCells?.map((cell, index) => (
                                <div key={index} className="flex items-center w-full">
                                    <div className="w-1/3">{cell.label}:</div>
                                    <div className="w-2/3">
                                        <Skeleton
                                            variant="rounded"
                                            height={20}
                                            animation="wave"
                                        />
                                    </div>
                                </div>
                            ))}
                        {rows?.map((row, index) => (
                            <div key={index} className="flex flex-col gap-2">
                                {headCells?.map((cell, idx) => (
                                    <div key={idx} className="flex items-center w-full">
                                        <div className="w-1/3">{cell.label}:</div>
                                        <div className="flex w-2/3">{row[cell.id]}</div>
                                    </div>
                                ))}
                                <GlobalDivider className="my-3 text-white" />
                            </div>
                        ))}
                    </div>
                </div>

                <NoData visible={rows.length < 1 && !isLoading} />

                <div className="mt-5">
                    <PaginationButtons
                        totalPage={totalPages}
                        page={page}
                        onChange={handlePageChange}
                    />
                </div>
            </div>
        </div>
    )
}

export default GlobalTable
